/* eslint no-param-reassign: "error" */
import React, {
  useEffect,
} from 'react';
import {
  useParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import {
  selection,
} from 'redux-resource-plugins';
import {
  getStatus,
} from 'redux-resource';
import i18n from 'i18next';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';
import {
  Button,
  Select,
  Avatar,
  Tooltip,
  Spinner,
  NotFound,
  Checkbox,
  Pagination,
  MoveToGroup,
  ContentWrapper,
} from 'web-components';
import {
  Flex,
  icons,
  Text,
} from 'web-styled';
import {
  OpenedTooltip,
} from 'web-types';

import * as S from './styled';
import Header from '../Header';

const mapState = (state => ({
  clients: coreSelectors.sortedSelectionResourceList('clients', 'clientList')(state),
  pageMeta: coreSelectors.getRequestProperties(
    'clients',
    'fetchIndividuals',
  )(state).meta,
  readStatus: getStatus(
    state,
    [
      'clients.requests.fetchIndividuals.status',
    ],
    true,
  ),
  selectedIds: coreSelectors.getSelectedIds('clients')(state),
  clientGroups: coreSelectors.sortedSelectionResourceList('clientGroups')(state),
  searchOptions: coreSelectors.getSearchOptions('clients')(state),
  groupsSelectedIds: coreSelectors.getSelectedIds('clientGroups')(state),
  ...getUiState([
    'currentEntityId',
    'moveEntityToGroupModal',
  ])(state),
}));

interface Props {
  isCustomer?: boolean
  openedTooltips: OpenedTooltip[]
  onChecked: { (data: any): void }
  onRequestAdd?: { (): void }
}

const Individuals = ({
  isCustomer,
  openedTooltips,
  onChecked,
  onRequestAdd,
}: Props) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    clients,
    pageMeta,
    readStatus,
    selectedIds,
    clientGroups,
    searchOptions,
    currentEntityId,
    moveEntityToGroupModal,
  } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();

  const page = params.page || '1';
  useEffect(() => {
    dispatch(
      coreActions.requestFetchClients({
        qs: {
          page,
          type: isCustomer ? 'customer' : 'client',
          per_page: 50,
        },
        list: 'clientList',
        requestKey: 'fetchIndividuals',
        resourceType: 'clients',
      }),
    );
  }, [isCustomer, page]);

  const onRequestArchive = (cls: any[]) => {
    const onConfirmAction = coreActions.requestArchiveClients(cls);

    const multiple = cls.length > 1;
    const bodyText = i18n.t(
      `client.archive${multiple ? '_multiple' : ''}_modal`,
    );

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: `Archive ${i18n.format(i18n.t('client', { count: cls.length }), 'cap')}`,
          bodyText,
          confirmText: 'Confirm',
        },
        onConfirmAction,
      }),
    );
  };

  const onRequestMoveItems = () => {
    dispatch(
      selection.clearSelectedResources('clientGroups'),
    );
    dispatch(
      uiActions.setUiState({
        moveEntityToGroupModal: true,
      }),
    );
  };

  const onRequestMoveEntity = (id: number) => {
    dispatch(selection.clearSelectedResources('clientGroups'));
    dispatch(uiActions.setUiState({
      currentEntityId: id,
      moveEntityToGroupModal: true,
    }));
  };

  const handleDeleteClient = (client: any) => {
    const fullName = `${client.user_profile.first_name} ${client.user_profile.last_name}`;
    const onConfirmAction = coreActions.requestDeleteClientEntity({
      id: client.id,
      resourceType: 'clients',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete User',
          bodyText: `Are you sure you want to delete <b>${fullName}</b>?`,
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };

  const handleDeleteItems = () => {
    const onConfirmAction = coreActions.requestDeleteClientItems({
      resourceType: 'clients',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: i18n.t('delete.client.modal.title', { count: selectedIds.length }),
          bodyText: i18n.t('delete.client.modal.body', { count: selectedIds.length }),
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };
  const items = clients.filter((c: any) => {
    if (isCustomer) {
      return !c.is_dummy && !c.invited_by_id;
    }

    return c.is_dummy || c.invited_by_id;
  });

  return (
    <ContentWrapper
      header={(
        <Header
          items={items}
          isCustomer={isCustomer}
          openedTooltips={openedTooltips}
          onRequestAdd={onRequestAdd}
          onRequestArchive={() => onRequestArchive(
            items.filter(c => c.selected),
          )}
          onRequestMoveItems={onRequestMoveItems}
          onRequestDeleteItems={handleDeleteItems}
          resourceType="clients"
        />
      )}
    >
      <Flex
        column
        fillSpace
        width="100%"
      >
        <S.Search>
          <Select
            value=""
            isClearable
            options={searchOptions}
            placeholder={`Search ${isCustomer ? 'customers' : 'individuals'}`}
            onChange={(item) => {
              if (item?.value) {
                navigate(`/users/${item.value}/programs`);
              }
            }}
          />
        </S.Search>
        <S.ClientsHeader>
          <Text
            size="18px"
            color="#9ea0a1"
            weight={500}
          >
            {isCustomer ? 'Customers' : 'Individuals'}
          </Text>
        </S.ClientsHeader>

        {readStatus.succeeded && (
          <>
            {items.length
              ? items.map(client => (
                <S.Item
                  key={client.id}
                >
                  <Flex
                    fillSpace
                    align="center"
                    justify="space-between"
                  >
                    <Flex
                      spacing={10}
                      align="center"
                    >
                      <Checkbox
                        checked={selectedIds.includes(client.id)}
                        onChange={() => onChecked(
                          {
                            id: client.id,
                            selectedIds,
                            resourceType: 'clients',
                          },
                        )}
                      />
                      <S.ClientLink
                        to={`/users/${client.id}/programs`}
                      >
                        <Avatar
                          width="28px"
                          height="28px"
                          src={client.user_profile?.avatar?.thumb?.url}
                        />
                        <S.Name>
                          {`${client.user_profile.first_name} ${client.user_profile.last_name}`}
                        </S.Name>
                      </S.ClientLink>
                    </Flex>
                    <S.MoveBar>
                      <Flex
                        spacing={10}
                        align="center"
                      >
                        <Tooltip
                          body={i18n.t('tooltip.client.move')}
                          positions={['left']}
                        >
                          <Button
                            ghost
                            appearance="primary"
                            onClick={() => onRequestMoveEntity(client.id)}
                          >
                            Move to group
                          </Button>
                        </Tooltip>
                        <Button
                          ghost
                          appearance="primary"
                          onClick={() => onRequestArchive([client])}
                        >
                          Archive
                        </Button>
                        <Button
                          ghost
                          appearance="danger"
                          onClick={() => handleDeleteClient(client)}
                        >
                          Remove
                        </Button>
                      </Flex>
                    </S.MoveBar>
                  </Flex>
                </S.Item>
              )) : (
                <S.Empty>
                  <img src={icons.folder} alt="" />
                  <span>
                    You dont have
                    <br />
                    anything inside this folder.
                  </span>
                  {!isCustomer && (
                    <Button
                      ghost
                      appearance="primary"
                      icon="fa fa-plus"
                      onClick={onRequestAdd}
                    >
                      Add client
                    </Button>
                  )}
                </S.Empty>
              )}

            {pageMeta?.total_pages && pageMeta?.total_pages > 1 && (
              <Flex padding="10px 0" justify="center" width="100%">
                <Pagination
                  count={pageMeta.total_pages}
                  current={pageMeta.page}
                  goToPage={(p: number) => {
                    const url = location.pathname.split('/').slice(1, 3).join('/');

                    navigate(`/${url}/${p}`);
                  }}
                />
              </Flex>
            )}
          </>
        )}

        {readStatus.pending && (
          <Spinner />
        )}

        {readStatus.failed && (
          <NotFound title="Something is wrong" />
        )}
      </Flex>

      <MoveToGroup
        isOpen={moveEntityToGroupModal}
        groups={clientGroups}
        onRequestClose={() => {
          dispatch(selection.clearSelectedResources('clientGroups'));
          dispatch(
            uiActions.setUiState({
              currentEntityId: null,
              moveEntityToGroupModal: false,
            }),
          );
        }}
        onChecked={onChecked}
        onSubmit={(currentId, actions) => {
          actions.onSuccess = () => {
            dispatch(
              uiActions.setUiState({
                currentEntityId: null,
                moveEntityToGroupModal: false,
              }),
            );
            dispatch(selection.clearSelectedResources('clientGroups'));
            dispatch(selection.clearSelectedResources('clients'));
          };
          dispatch(
            coreActions.requestMoveToGroup(
              actions,
              currentEntityId,
            ),
          );
        }}
      />
    </ContentWrapper>
  );
};

Individuals.defaultProps = {
  isCustomer: false,
  onRequestAdd: undefined,
};

export default Individuals;
